import { ConfirmationDialog, ConfirmationDialogProps, TranslationFuncType } from "@itk/mui-components";
import { useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { applicationActions } from "../application-store";
import { OmitSafe, hasValue } from "@itk/typescript-utils";
import { WarningAction } from "./warning-dialog-types";
import { useGenericTranslationsT } from "src/translation-hooks/generic-translations";

const ownActions = {
  setWarningAction: applicationActions.setWarningAction
};

type Props<TObject extends TranslationFuncType> = typeof ownActions & OmitSafe<ConfirmationDialogProps<TObject>, "onCancel" | "onConfirm" | "onDecline"> & { warningAction: WarningAction };

const WarningDialogBase = <TObject extends TranslationFuncType>(props: Props<TObject>) => {
  const { warningAction, setWarningAction } = props;
  const { defaultTranslation = "default" } = warningAction;

  const genericTranslationsT = useGenericTranslationsT();

  const onConfirm = useCallback(() => {
    if (hasValue(warningAction?.confirm)) {
      warningAction?.confirm();
    }
  }, [warningAction, setWarningAction]);

  const onDecline = useMemo(() => {
    switch (warningAction.variant) {
      case "confirmDecline":
      case "confirmDeclineCancel":
        return () => {
          setWarningAction(undefined);
          warningAction.decline?.();
        };
      case "confirmCancel":
      case "confirmOnly":
        return undefined;
      default:
        const assertNever: never = warningAction;
        return assertNever;
    }
  }, [warningAction, setWarningAction]);

  const onCancel = useMemo(() => {
    switch (warningAction.variant) {
      case "confirmDeclineCancel":
      case "confirmCancel":
        return () => {
          setWarningAction(undefined);
          warningAction.cancel?.();
        };
      case "confirmDecline":
      case "confirmOnly":
        return undefined;
      default:
        const assertNever: never = warningAction;
        return assertNever;
    }
  }, [warningAction, setWarningAction]);

  // We are using a switch here to easily add new default translations later
  const message = useMemo(() => {
    if (!hasValue(defaultTranslation)) return undefined;
    if (hasValue(warningAction?.dialogProps?.message)) {
      return warningAction.dialogProps?.message;
    }
    switch (defaultTranslation) {
      case "saveFirst":
        return genericTranslationsT.saveFirst;
      case "default":
        return undefined;
      default:
        const assertNever: never = defaultTranslation;
        return assertNever;
    }
  }, [warningAction?.defaultTranslation, warningAction?.dialogProps?.message]);

  const yesButtonTranslation = useMemo(() => {
    if (!hasValue(defaultTranslation)) return undefined;
    if (hasValue(warningAction?.dialogProps?.confirmButtonTranslation)) {
      return warningAction?.dialogProps?.confirmButtonTranslation;
    }
    switch (defaultTranslation) {
      case "saveFirst":
      case "default":
        return undefined;
      default:
        const assertNever: never = defaultTranslation;
        return assertNever;
    }
  }, [warningAction?.defaultTranslation, warningAction?.dialogProps?.confirmButtonTranslation]);

  const noButtonTranslation = useMemo(() => {
    if (!hasValue(defaultTranslation)) return undefined;
    if (hasValue(warningAction?.dialogProps?.cancelButtonTranslation)) {
      return warningAction?.dialogProps?.cancelButtonTranslation;
    }
    switch (defaultTranslation) {
      case "saveFirst":
      case "default":
        return undefined;
      default:
        const assertNever: never = defaultTranslation;
        return assertNever;
    }
  }, [warningAction?.defaultTranslation, warningAction?.dialogProps?.cancelButtonTranslation]);

  const cancelButtonTranslation = useMemo(() => {
    if (!hasValue(defaultTranslation)) return undefined;
    if (hasValue(warningAction?.dialogProps?.cancelButtonTranslation)) {
      return warningAction?.dialogProps?.cancelButtonTranslation;
    }
    switch (defaultTranslation) {
      case "saveFirst":
      case "default":
        return undefined;
      default:
        const assertNever: never = defaultTranslation;
        return assertNever;
    }
  }, [warningAction?.defaultTranslation, warningAction?.dialogProps?.cancelButtonTranslation]);

  return (
    <ConfirmationDialog
      disableDraggable
      onConfirm={onConfirm}
      onDecline={onDecline}
      onCancel={onCancel}
      message={message}
      messageIcon={warningAction?.dialogProps?.messageIcon}
      confirmButtonTranslation={yesButtonTranslation}
      declineButtonTranslation={noButtonTranslation}
      cancelButtonTranslation={cancelButtonTranslation}
      focusButton={warningAction?.dialogProps?.focusButton}
    />
  );
};

export const WarningDialog = connect(undefined, ownActions)(WarningDialogBase);
